import { formatCurrency } from './currency';
import { toApiUrl } from '../lib/apiOrigin';

// Texte de publication qui accompagne le lien d'un appel à projets
// (WhatsApp, LinkedIn, e-mail…). Texte brut : aucun emoji, aucune mise en forme.
//
// Deux liens :
// - la page de partage Innovactive360 vient en premier : c'est elle que WhatsApp et LinkedIn
//   affichent en aperçu (titre, résumé, affiche) ;
// - le lien de candidature de l'organisateur ensuite, car la plupart des appels sont lancés par
//   d'autres organismes, seuls à recevoir les candidatures. Quand Innovactive360 les reçoit
//   (appel lancé par nous ou partenaire qui l'autorise), on ne donne que notre lien.

const TYPE_LABELS = {
  financement: 'Financement',
  formation: 'Formation',
  concours: 'Concours',
  partenariat: 'Partenariat',
  emploi: 'Emploi',
};

export const EXCERPT_MAX = 280;

const DAY_MS = 24 * 60 * 60 * 1000;

const clean = (value) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');

// Début de la description : phrases entières tant qu'elles tiennent,
// sinon coupure au dernier mot complet suivie de « … ».
export const excerpt = (text, max = EXCERPT_MAX) => {
  const flat = clean(text);
  if (flat.length <= max) return flat;
  const sentences = flat.match(/[^.!?]+[.!?]+(\s|$)/g) || [];
  let out = '';
  for (const sentence of sentences) {
    if ((out + sentence).trim().length > max) break;
    out += sentence;
  }
  out = out.trim();
  if (out) return out;
  const cut = flat.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:–-]+$/, '')}…`;
};

const formatShortDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const deadlineLine = (deadline, now = new Date()) => {
  const date = formatShortDate(deadline);
  if (!date) return '';
  const days = Math.ceil((new Date(deadline) - now) / DAY_MS);
  if (days <= 0) return `Date limite : ${date} (candidatures clôturées)`;
  if (days === 1) return `Date limite : ${date} (dernier jour)`;
  return `Date limite : ${date} (encore ${days} jours)`;
};

/**
 * Compose le texte de partage.
 * @param {object} project  appel à projets tel que renvoyé par l'API
 *                          (receivesApplications, applicationUrl calculés par le serveur)
 * @param {string} url      lien de la page de partage Innovactive360 (aperçu)
 * @param {object} options  { now = new Date() }
 */
export const buildShareText = (project, url, { now = new Date() } = {}) => {
  if (!project) return url || '';
  const lines = [`Appel à projets : ${clean(project.title)}`];

  const meta = [TYPE_LABELS[project.type] || clean(project.type), clean(project.category), clean(project.location?.country)]
    .filter(Boolean);
  if (meta.length) lines.push(meta.join(' · '));

  const budget = Number(project.budget);
  if (Number.isFinite(budget) && budget > 0) lines.push(`Budget : ${formatCurrency(budget, project.currency)}`);

  const deadline = deadlineLine(project.deadline, now);
  if (deadline) lines.push(deadline);

  const summary = excerpt(project.description);
  if (summary) lines.push('', summary);

  const closed = deadline.includes('clôturées');
  const external = !project.receivesApplications && project.applicationUrl;
  lines.push('');
  if (closed) {
    if (url) lines.push(`Voir l'appel à projets : ${url}`);
  } else if (project.receivesApplications) {
    if (url) lines.push(`Candidater sur Innovactive360 : ${url}`);
  } else {
    if (url) lines.push(`En savoir plus : ${url}`);
    lines.push(external
      ? `Pour candidater, rendez-vous sur le site de l'organisateur : ${project.applicationUrl}`
      : "Pour candidater, suivez les indications de l'organisateur sur la page de l'appel.");
  }
  while (lines[lines.length - 1] === '') lines.pop();
  return lines.join('\n');
};

/** Absolu, même en développement (où toApiUrl laisse les chemins relatifs). */
const absolu = (path) => {
  const url = toApiUrl(path);
  return /^https?:\/\//.test(url) ? url : `${window.location.origin}${url}`;
};

/**
 * Page de partage Innovactive360 (balises d'aperçu, puis redirection vers l'appel).
 * Le serveur fournit l'adresse publique (project.shareUrl : courte si Nginx est configuré) ;
 * à défaut, adresse d'API construite ici.
 */
export const sharePageUrl = (project) => project?.shareUrl
  || absolu(`/api/partage/appels/${encodeURIComponent(project?.id ?? '')}`);

/** Affiche de l'appel. Formats : carre (1080×1080), portrait (1080×1350), story (1080×1920). */
export const posterUrl = (project, format) => {
  const version = project?.updatedAt ? `&v=${encodeURIComponent(project.updatedAt)}` : '';
  return `${sharePageUrl(project)}/affiche.png?format=${encodeURIComponent(format)}${version}`;
};
